import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { baseApiURL } from "../../baseUrl";
import {
  FaFileExcel,
  FaFilePdf,
  FaDownload,
  FaBed,
  FaIdCard,
  FaClock,
  FaMoneyBillWave,
  FaTools,
  FaFilter,
  FaSpinner,
  FaSearch,
  FaGraduationCap,
  FaBook,
  FaLayerGroup,
  FaRedo,
} from "react-icons/fa";

const HostelReports = () => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState("occupancy");
  const [reportsData, setReportsData] = useState(null);

  // Student academic & demographic filters
  const [filterSemester, setFilterSemester] = useState("all");
  const [filterRegulation, setFilterRegulation] = useState("all");
  const [filterBatch, setFilterBatch] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseApiURL()}/hostel/reports?reportType=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportsData(res.data?.reports);
    } catch (err) {
      toast.error("Failed to load reports data");
    } finally {
      setLoading(false);
    }
  };

  // Extract unique regulations and batches dynamically from all datasets
  const { uniqueRegulations, uniqueBatches, uniqueBranches } = useMemo(() => {
    const regs = new Set();
    const batches = new Set();
    const branches = new Set();

    if (reportsData) {
      Object.values(reportsData).forEach((list) => {
        if (Array.isArray(list)) {
          list.forEach((item) => {
            if (item.regulation && item.regulation !== "-") regs.add(item.regulation);
            if (item.batch && item.batch !== "-") batches.add(item.batch);
            if (item.branch && item.branch !== "-" && item.branch !== "N/A") branches.add(item.branch);
          });
        }
      });
    }

    return {
      uniqueRegulations: Array.from(regs),
      uniqueBatches: Array.from(batches),
      uniqueBranches: Array.from(branches),
    };
  }, [reportsData]);

  // Filter current active dataset
  const filteredActiveData = useMemo(() => {
    if (!reportsData || !reportsData[reportType]) return [];

    return reportsData[reportType].filter((item) => {
      // Semester filter
      if (filterSemester !== "all") {
        const itemSem = String(item.semester || "").toLowerCase();
        if (!itemSem.includes(`sem ${filterSemester.toLowerCase()}`) && !itemSem.includes(filterSemester.toLowerCase())) {
          return false;
        }
      }

      // Regulation filter
      if (filterRegulation !== "all") {
        if (item.regulation !== filterRegulation) return false;
      }

      // Batch filter
      if (filterBatch !== "all") {
        const itemBatch = String(item.batch || "");
        if (!itemBatch.includes(filterBatch)) return false;
      }

      // Branch filter
      if (filterBranch !== "all") {
        if (item.branch !== filterBranch) return false;
      }

      // Search Query
      if (filterSearch.trim()) {
        const q = filterSearch.toLowerCase().trim();
        const strValues = Object.values(item).map((v) => String(v).toLowerCase()).join(" ");
        if (!strValues.includes(q)) return false;
      }

      return true;
    });
  }, [reportsData, reportType, filterSemester, filterRegulation, filterBatch, filterBranch, filterSearch]);

  const resetFilters = () => {
    setFilterSemester("all");
    setFilterRegulation("all");
    setFilterBatch("all");
    setFilterBranch("all");
    setFilterSearch("");
  };

  const exportToExcel = (dataType, fileName) => {
    const dataToExport = dataType === reportType ? filteredActiveData : (reportsData?.[dataType] || []);
    if (!dataToExport || dataToExport.length === 0) {
      toast.error("No data available to export matching your filters");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, fileName.slice(0, 31));

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const activeFilterSuffix = filterSemester !== "all" ? `_Sem${filterSemester}` : "";
    saveAs(dataBlob, `${fileName}${activeFilterSuffix}_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success(`Exported ${fileName}.xlsx (${dataToExport.length} rows) successfully!`);
  };

  const printReport = () => {
    window.print();
  };

  const reportCards = [
    {
      id: "occupancy",
      title: "Bed Occupancy & Allocation Master",
      desc: "Room-by-room inventory, occupied vs vacant beds, semester, regulation, and batch details.",
      count: reportsData?.occupancy?.length || 0,
      icon: FaBed,
      color: "from-rose-500 to-amber-600",
      fileName: "Hostel_Occupancy_Master",
    },
    {
      id: "gatepass",
      title: "Gate Pass & Curfew Overdue Ledger",
      desc: "All Outings and Leaves filtered by semester and batch with curfew tracking.",
      count: reportsData?.gatepass?.length || 0,
      icon: FaIdCard,
      color: "from-amber-500 to-orange-600",
      fileName: "Hostel_GatePass_Violations_Ledger",
    },
    {
      id: "attendance",
      title: "Night Roll Call Attendance Log",
      desc: "Official audit sheet of present, absent, on-leave, and late students with semester metrics.",
      count: reportsData?.attendance?.length || 0,
      icon: FaClock,
      color: "from-blue-600 to-indigo-600",
      fileName: "Hostel_Night_RollCall_Attendance",
    },
    {
      id: "finance",
      title: "Hostel Fee Collections & Defaulters",
      desc: "Room rent, mess fee, caution deposit, collections, and dues broken down by batch & semester.",
      count: reportsData?.finance?.length || 0,
      icon: FaMoneyBillWave,
      color: "from-emerald-500 to-teal-600",
      fileName: "Hostel_Fee_Collections_Defaulters",
    },
    {
      id: "maintenance",
      title: "Maintenance & SLA Compliance Log",
      desc: "Tickets by category, resolution turnaround times, technician logs, and student ratings.",
      count: reportsData?.maintenance?.length || 0,
      icon: FaTools,
      color: "from-purple-500 to-pink-600",
      fileName: "Hostel_Maintenance_SLA_Report",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FaFileExcel className="text-emerald-600" /> Academic & Batch Hostel Reports Suite
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">
            Audit-compliant spreadsheet generation with Semester, Regulation, Batch, and Department filters
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={printReport}
            className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer flex items-center gap-1.5 shadow-xs transition"
          >
            <FaFilePdf className="text-rose-500" /> Print / Save PDF
          </button>
          <button
            onClick={fetchReportData}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 cursor-pointer flex items-center gap-1.5 transition disabled:opacity-50"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaRedo />} Refresh Data
          </button>
        </div>
      </div>

      {/* Reports Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reportCards.map((rc) => {
          const Icon = rc.icon;
          return (
            <div
              key={rc.id}
              onClick={() => setReportType(rc.id)}
              className={`p-5 rounded-2xl border bg-white dark:bg-slate-900 shadow-sm transition cursor-pointer flex flex-col justify-between space-y-4 ${
                reportType === rc.id
                  ? "border-rose-500 ring-2 ring-rose-500/20"
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${rc.color} text-white shadow-md`}>
                  <Icon className="text-lg" />
                </div>
                <span className="text-xs font-black px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono">
                  {rc.count} Total
                </span>
              </div>

              <div>
                <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100">{rc.title}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{rc.desc}</p>
              </div>

              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToExcel(rc.id, rc.fileName);
                  }}
                  className="w-full py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-xs transition flex items-center justify-center gap-1.5"
                >
                  <FaFileExcel /> Export .XLSX
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Control Center */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <FaFilter className="text-rose-500 text-xs" />
            <span className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Academic & Student Filters
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">
              Matching Records: <strong className="text-rose-600">{filteredActiveData.length}</strong>
            </span>
            {(filterSemester !== "all" || filterRegulation !== "all" || filterBatch !== "all" || filterBranch !== "all" || filterSearch) && (
              <button
                onClick={resetFilters}
                className="text-[11px] font-bold text-rose-500 hover:underline cursor-pointer ml-2"
              >
                Reset All Filters
              </button>
            )}
          </div>
        </div>

        {/* Filter Dropdowns Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {/* Semester Filter */}
          <div>
            <label className="font-bold text-slate-600 dark:text-slate-400 block mb-1 flex items-center gap-1">
              <FaGraduationCap className="text-indigo-500" /> Semester:
            </label>
            <select
              value={filterSemester}
              onChange={(e) => setFilterSemester(e.target.value)}
              className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-semibold cursor-pointer text-slate-800 dark:text-slate-200"
            >
              <option value="all">All Semesters (1 - 8)</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                <option key={s} value={String(s)}>
                  Semester {s}
                </option>
              ))}
            </select>
          </div>

          {/* Regulation Filter */}
          <div>
            <label className="font-bold text-slate-600 dark:text-slate-400 block mb-1 flex items-center gap-1">
              <FaBook className="text-amber-500" /> Regulation:
            </label>
            <select
              value={filterRegulation}
              onChange={(e) => setFilterRegulation(e.target.value)}
              className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-semibold cursor-pointer text-slate-800 dark:text-slate-200"
            >
              <option value="all">All Regulations</option>
              {uniqueRegulations.map((reg) => (
                <option key={reg} value={reg}>
                  {reg}
                </option>
              ))}
              {!uniqueRegulations.includes("R22") && <option value="R22">R22</option>}
              {!uniqueRegulations.includes("R20") && <option value="R20">R20</option>}
              {!uniqueRegulations.includes("R18") && <option value="R18">R18</option>}
            </select>
          </div>

          {/* Batch Filter */}
          <div>
            <label className="font-bold text-slate-600 dark:text-slate-400 block mb-1 flex items-center gap-1">
              <FaLayerGroup className="text-emerald-500" /> Batch / Year:
            </label>
            <select
              value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)}
              className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-semibold cursor-pointer text-slate-800 dark:text-slate-200"
            >
              <option value="all">All Batches</option>
              {uniqueBatches.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
              {["2022", "2023", "2024", "2025", "2026"].map((yr) => (
                !uniqueBatches.some((b) => b.includes(yr)) && (
                  <option key={yr} value={yr}>
                    Batch {yr}
                  </option>
                )
              ))}
            </select>
          </div>

          {/* Branch / Dept Filter */}
          <div>
            <label className="font-bold text-slate-600 dark:text-slate-400 block mb-1">
              Branch / Department:
            </label>
            <select
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
              className="w-full p-2 rounded-xl border bg-slate-50 dark:bg-slate-800 dark:border-slate-700 font-semibold cursor-pointer text-slate-800 dark:text-slate-200"
            >
              <option value="all">All Branches</option>
              {uniqueBranches.map((br) => (
                <option key={br} value={br}>
                  {br}
                </option>
              ))}
              {!uniqueBranches.includes("CSE") && <option value="CSE">CSE</option>}
              {!uniqueBranches.includes("ECE") && <option value="ECE">ECE</option>}
              {!uniqueBranches.includes("IT") && <option value="IT">IT</option>}
              {!uniqueBranches.includes("MECH") && <option value="MECH">MECH</option>}
              {!uniqueBranches.includes("CIVIL") && <option value="CIVIL">CIVIL</option>}
              {!uniqueBranches.includes("AI&DS") && <option value="AI&DS">AI&DS</option>}
            </select>
          </div>
        </div>

        {/* Real-time Search Input */}
        <div className="relative">
          <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
          <input
            type="text"
            placeholder="Search across all fields (Student name, Roll number, Room, Bed, Phone)..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            className="w-full pl-9 pr-8 py-2 rounded-xl text-xs border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-hidden"
          />
          {filterSearch && (
            <button
              onClick={() => setFilterSearch("")}
              className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Interactive Data Preview Table */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span>Report Table:</span>
              <span className="text-rose-600 uppercase font-mono font-black">
                {reportCards.find((r) => r.id === reportType)?.title}
              </span>
            </h3>
            <p className="text-[11px] text-slate-500">
              Showing {filteredActiveData.length} records matching active filters
            </p>
          </div>

          <button
            onClick={() => {
              const current = reportCards.find((r) => r.id === reportType);
              if (current) exportToExcel(current.id, current.fileName);
            }}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer shadow-xs flex items-center gap-1.5 transition"
          >
            <FaDownload /> Download Filtered Excel (.XLSX)
          </button>
        </div>

        {/* Dynamic Table */}
        <div className="overflow-x-auto max-h-[420px] rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-xs text-slate-600 dark:text-slate-400">
            <thead className="bg-slate-50 dark:bg-slate-800/80 sticky top-0 text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-800">
              <tr>
                {filteredActiveData[0] &&
                  Object.keys(filteredActiveData[0]).map((key) => (
                    <th key={key} className="p-3 whitespace-nowrap">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredActiveData.length === 0 ? (
                <tr>
                  <td colSpan="12" className="p-8 text-center text-xs text-slate-500">
                    No records match the selected Semester, Regulation, or Batch filters.
                  </td>
                </tr>
              ) : (
                filteredActiveData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition">
                    {Object.entries(row).map(([k, val], cIdx) => (
                      <td key={cIdx} className="p-3 whitespace-nowrap text-slate-800 dark:text-slate-200">
                        {k === "semester" || k === "regulation" || k === "batch" ? (
                          <span className="font-bold font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {String(val)}
                          </span>
                        ) : typeof val === "object" ? (
                          JSON.stringify(val)
                        ) : (
                          String(val)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HostelReports;
